import { ChartColumnBig, type LucideIcon, Table2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /**
   * Optional leading glyph. Icons make the options distinguishable at a glance
   * without reading the label — worth it on the view switchers, noise on the
   * unit ones, where "# hrs" and "%" are already glyph-like.
   */
  icon?: LucideIcon;
}

/**
 * Compact segmented control for a widget's header — the `[Chart|Table]`,
 * `[# hrs|%]` and `[Firm|Location|Segment]` switchers are all this component.
 *
 * Deliberately not shadcn `Tabs`: tabs own a panel and carry roled markup for
 * switching content regions, while these only change how the *same* content is
 * rendered.
 *
 * Exposed as a labelled group of `aria-pressed` toggle buttons rather than a
 * radiogroup: the options are pressed states of one control, not a set of form
 * values to submit, and screen readers announce the pressed state without the
 * roving-tabindex behaviour a radiogroup implies.
 */
export function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Accessible name for the group, e.g. "View as". */
  label: string;
  className?: string;
}) {
  return (
    // `fieldset` gives the grouping semantics natively — `aria-label` names it
    // without a visible legend, which wouldn't fit a widget header.
    <fieldset
      aria-label={label}
      className={cn('inline-flex items-center gap-0.5 rounded-md bg-muted p-0.5', className)}
    >
      {options.map((o) => {
        const selected = o.value === value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.value)}
            className={cn(
              'rounded-sm px-2 py-1 text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span className="inline-flex items-center gap-1">
              {Icon ? <Icon className="size-3.5" aria-hidden /> : null}
              {o.label}
            </span>
          </button>
        );
      })}
    </fieldset>
  );
}

/** `[Chart|Table]` — every tile can render either. */
export type ChartTableView = 'chart' | 'table';
export function ChartTableToggle({
  value,
  onChange,
}: {
  value: ChartTableView;
  onChange: (v: ChartTableView) => void;
}) {
  return (
    <SegmentedToggle
      label="View as"
      value={value}
      onChange={onChange}
      options={[
        { value: 'chart', label: 'Chart', icon: ChartColumnBig },
        { value: 'table', label: 'Table', icon: Table2 },
      ]}
    />
  );
}

/** `[# hrs|%]` — absolute hours vs share of available capacity. */
export type UnitMode = 'hours' | 'percent';
export function UnitToggle({
  value,
  onChange,
}: {
  value: UnitMode;
  onChange: (v: UnitMode) => void;
}) {
  return (
    <SegmentedToggle
      label="Units"
      value={value}
      onChange={onChange}
      options={[
        { value: 'hours', label: '# hrs' },
        { value: 'percent', label: '%' },
      ]}
    />
  );
}
